import React from 'react';
import { EmptyState } from '@parsina/framework';
import { C } from '@parsina/framework/tokens';
import { TrendingUp } from 'lucide-react';

import { useTranslation } from '../i18n';

/**
 * Grouped bars over time — one group per bucket, one bar per series.
 *
 * ── DIRECTION IS A PROP OF THE READER, NOT OF THE GEOMETRY ──────────────────
 * Time runs toward the reading direction: earliest at the inline-START, so at the
 * right in Persian and at the left in English. The predecessor hard-coded the RTL
 * layout into its arithmetic (`groupRight = width - PAD.right - gi * groupW`),
 * which is not a thing a `dir` flip can fix — under `ltr` it drew the series
 * backwards, with the newest month first. Here the bucket array is ordered for
 * the reader and then laid out left-to-right by one formula, so there is only one
 * geometry to be right.
 *
 * ── ONE Y-AXIS, ALWAYS ──────────────────────────────────────────────────────
 * Two scales on one chart is the single most common charting mistake and it makes
 * every comparison between the series a lie. Revenue is a SEPARATE chart for
 * exactly this reason: it shares the x-domain and nothing else.
 *
 * ── THE VALUE LABELS ARE NOT DECORATION ─────────────────────────────────────
 * The palette was validated computationally rather than chosen by eye — lightness
 * band, chroma floor, and worst adjacent CVD ΔE all pass — but two of the hues sit
 * below 3:1 against the card surface. That is a WARN, not a dismissal, and it
 * obligates visible relief: hence a number above every bar and a legend that is
 * always present. Identity is never carried by colour alone here.
 *
 * ── AND IT IS OPERABLE ──────────────────────────────────────────────────────
 * Every bar is a focusable `<g role="img">` with its own accessible name, so the
 * values are reachable by keyboard and readable by a screen reader. Hover-only
 * was the previous behaviour, which meant the chart's actual numbers existed for
 * mouse users exclusively.
 *
 * Props:
 *   buckets     [{ label, ...seriesKeys }] in CHRONOLOGICAL order
 *   series      [{ key, label, color }]
 *   formatValue (n) => string — axis ticks, bar labels, the accessible name
 *   formatBucket(label) => string
 *   ariaLabel   names the whole figure
 *   height      px, default 240
 */

const PAD_BLOCK = { top: 28, bottom: 34 };
const AXIS_GUTTER = 44;   // room for the y-axis labels, on the inline-start side
const EDGE_GUTTER = 12;
const BAR_GAP = 2;
const GROUP_GAP = 26;
const RADIUS = 4;
const TICKS = 5;

export default function GrowthChart({
  buckets = [],
  series = [],
  formatValue,
  formatBucket,
  ariaLabel,
  height = 240,
  emptyTitle,
  emptyDescription,
  'data-testid': testId = 'growth-chart',
}) {
  const { t, isRTL, formatNumber } = useTranslation();
  const [active, setActive] = React.useState(null);

  const fmtValue = formatValue || formatNumber;
  const fmtBucket = formatBucket || ((label) => label);

  if (!buckets.length || !series.length) {
    return (
      <EmptyState
        icon={<TrendingUp size={44} strokeWidth={1.5} aria-hidden />}
        title={emptyTitle || t('dashboard.chart.emptyTitle')}
        description={emptyDescription || t('dashboard.chart.emptyDesc')}
        data-testid={`${testId}-empty`}
      />
    );
  }

  // Ordered FOR THE READER: earliest at the inline-start. Everything below then
  // lays out left-to-right, which is the only direction SVG coordinates have.
  const ordered = isRTL ? [...buckets].reverse() : buckets;

  // The axis sits at the inline-start edge, so its gutter swaps sides with the
  // reading direction rather than being a fixed `left`.
  const padStart = isRTL ? EDGE_GUTTER : AXIS_GUTTER;
  const padEnd = isRTL ? AXIS_GUTTER : EDGE_GUTTER;

  const max = Math.max(1, ...ordered.flatMap((b) => series.map((s) => Number(b[s.key]) || 0)));
  const width = Math.max(320, ordered.length * 110);
  const plotH = height - PAD_BLOCK.top - PAD_BLOCK.bottom;
  const plotW = width - padStart - padEnd;
  const groupW = plotW / ordered.length;
  const barW = Math.max(6, (groupW - GROUP_GAP - BAR_GAP * (series.length - 1)) / series.length);

  // Four recessive gridlines at values a human would say out loud. Keyed by
  // INDEX, not by value: with a small max the rounded values repeat (max=1 gives
  // 0,0,1,1,1) and React discarded the duplicates, which is a real warning the
  // test suite was printing on every run.
  const ticks = Array.from({ length: TICKS }, (_, i) => ({
    i,
    value: Math.round((max / (TICKS - 1)) * i),
  }));

  const axisX = isRTL ? width - padEnd + 4 : padStart - 6;
  const axisAnchor = isRTL ? 'start' : 'end';

  return (
    <div data-testid={testId}>
      {/* Only the chart scrolls sideways — never the page (§4.2). */}
      <div className="overflow-x-auto">
        <svg width={width} height={height} role="img" aria-label={ariaLabel}>
          {ticks.map(({ i, value }) => {
            const y = PAD_BLOCK.top + plotH - (value / max) * plotH;
            return (
              <g key={i}>
                <line
                  x1={padStart} x2={width - padEnd} y1={y} y2={y}
                  stroke={C.border} strokeWidth="1"
                />
                <text
                  x={axisX} y={y + 4}
                  fontSize="10" fill={C.textMuted} textAnchor={axisAnchor}
                >
                  {fmtValue(value)}
                </text>
              </g>
            );
          })}

          {ordered.map((bucket, gi) => {
            const groupStart = padStart + gi * groupW;
            return (
              <g key={bucket.label}>
                {series.map((s, si) => {
                  const value = Number(bucket[s.key]) || 0;
                  const barH = value === 0 ? 0 : Math.max(2, (value / max) * plotH);
                  const x = groupStart + GROUP_GAP / 2 + si * (barW + BAR_GAP);
                  const y = PAD_BLOCK.top + plotH - barH;
                  const isActive = active?.bucket === bucket.label && active?.key === s.key;
                  const name = t('dashboard.chart.point', {
                    bucket: fmtBucket(bucket.label),
                    series: s.label,
                    value: fmtValue(value),
                  });

                  return (
                    <g
                      key={s.key}
                      role="img"
                      aria-label={name}
                      tabIndex={0}
                      className="outline-none focus-visible:[&>rect]:stroke-2"
                      onMouseEnter={() => setActive({ bucket: bucket.label, key: s.key, value, series: s })}
                      onMouseLeave={() => setActive(null)}
                      onFocus={() => setActive({ bucket: bucket.label, key: s.key, value, series: s })}
                      onBlur={() => setActive(null)}
                    >
                      {barH > 0 ? (
                        <rect
                          x={x} y={y} width={barW} height={barH}
                          // Rounded data-end only; the base stays anchored to the
                          // axis so bar heights remain comparable.
                          rx={Math.min(RADIUS, barW / 2)}
                          fill={s.color}
                          stroke={C.textPrimary}
                          strokeWidth={0}
                          opacity={active && !isActive ? 0.45 : 1}
                        />
                      ) : (
                        // A zero still needs a focus target, or the keyboard path
                        // skips the months where nothing happened — which are
                        // exactly the ones an operator is looking for.
                        <rect
                          x={x} y={PAD_BLOCK.top + plotH - 2} width={barW} height={2}
                          fill={C.border}
                        />
                      )}
                      {value > 0 ? (
                        <text
                          x={x + barW / 2} y={y - 5}
                          fontSize="10" fill={C.textSecondary} textAnchor="middle"
                        >
                          {fmtValue(value)}
                        </text>
                      ) : null}
                    </g>
                  );
                })}
                <text
                  x={groupStart + groupW / 2} y={height - 12}
                  fontSize="11" fill={C.textSecondary} textAnchor="middle"
                >
                  {fmtBucket(bucket.label)}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      {/* The legend is ALWAYS present for ≥2 series — identity is never
          colour-alone (see the palette note above). */}
      {series.length > 1 && (
        <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2">
          {series.map((s) => (
            <span key={s.key} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              <span
                className="inline-block h-2.5 w-2.5 rounded-sm"
                style={{ background: s.color }}
                aria-hidden="true"
              />
              {s.label}
            </span>
          ))}
          {active ? (
            <span className="text-xs font-semibold text-foreground" data-testid={`${testId}-hover`}>
              {t('dashboard.chart.point', {
                bucket: fmtBucket(active.bucket),
                series: active.series.label,
                value: fmtValue(active.value),
              })}
            </span>
          ) : null}
        </div>
      )}
    </div>
  );
}
